import React from 'react'
import { useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import LoadingButton from 'components/loading-button'
import { useSnackbar } from 'notistack'
import { get } from 'lodash'

const FIELD_MARGIN_BOTTOM = '32px'

const InputField = ({ label, name, register, errors, multiline }) => (
  <FormControl variant="outlined" style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
    <Typography variant="caption" gutterBottom>
      <label htmlFor={name}>{label}</label>
    </Typography>
    <TextField
      name={name}
      error={!!errors[name]}
      helperText={errors[name] && errors[name].message}
      inputRef={register}
      multiline
      inputProps={{ style: { height: multiline ? '150px' : 'initial' } }}
      id={name}
    />
  </FormControl>
)

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .label('Name')
    .required(),
  email: yup
    .string()
    .label('Your Email Address')
    .required()
    .email(),
  phone: yup
    .string()
    .label('Phone Number')
    .matches(/^\d{3}-?\d{3}-?\d{4}$/, {
      message: 'Phone Number must be a valid phone number',
      excludeEmptyString: true,
    }),
  preferredContactMethod: yup
    .string()
    .label('Preferred contact method')
    .required()
    .oneOf(
      ['EMAIL', 'PHONE'],
      'Preferred contact method must be one of the available options',
    ),
  message: yup
    .string()
    .label('Message')
    .required(),
})

const defaultValues = {
  name: '',
  email: '',
  phone: '',
  preferredContactMethod: 'EMAIL',
  message: '',
}

const SEND_ERROR_MESSAGE = 'Error, unable to send message'

const ContactCoachForm = ({
  coachId,
  onCancel,
  mutation,
  resultObjectName,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { register, handleSubmit, errors, control } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })
  const [contactCoach, { error, data, loading }] = useMutation(mutation)

  const onSubmit = data => {
    contactCoach({ variables: { input: { ...data, coachId } } }).catch(a => a)
  }

  if (get(data, [resultObjectName, 'success'])) {
    enqueueSnackbar('Message Sent!', {
      variant: 'success',
      preventDuplicate: true,
    })
    onCancel()
  }

  if (error) {
    enqueueSnackbar(SEND_ERROR_MESSAGE, {
      variant: 'error',
      preventDuplicate: true,
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputField
        label="Name"
        name="name"
        errors={errors}
        register={register}
        id="name"
      />
      <InputField
        label="Your Email Address"
        name="email"
        errors={errors}
        register={register}
      />
      <InputField
        label="Phone Number (Optional)"
        name="phone"
        errors={errors}
        register={register}
      />
      <FormControl style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <Typography variant="caption" gutterBottom>
          <label id="preferredContactMethod">
            Your preferred method of contact?
          </label>
        </Typography>
        <Controller
          as={
            <Select labelId="preferredContactMethod">
              <MenuItem value="EMAIL">Email</MenuItem>
              <MenuItem value="PHONE">Phone</MenuItem>
            </Select>
          }
          name="preferredContactMethod"
          control={control}
        />
      </FormControl>
      <InputField
        label="Message"
        name="message"
        errors={errors}
        register={register}
        multiline
      />
      <Box display="flex" flexDirection="row" flexGrow={1}>
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          loading={loading}
        >
          Send
        </LoadingButton>
        <Button variant="text" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </Box>
    </form>
  )
}

export default ContactCoachForm
